import { useRef, useState } from "react";
import {
  Download,
  Settings,
  Plus,
  Upload,
  Layers,
  Palette,
  Sliders,
  Sparkles,
  Undo2,
  Box,
  Eye,
  ChevronDown,
  HelpCircle,
  ImageIcon,
  UploadCloud,
  Users,
  Mountain,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { ZoomCanvas } from "@/components/height-map/ZoomCanvas";
import { PaletteStrip } from "@/components/height-map/PaletteStrip";
import { HeightLayerRow } from "@/components/height-map/HeightLayerRow";
import { HowItWorks } from "@/components/height-map/HowItWorks";
import { PaintCanvas } from "@/components/height-map/PaintCanvas";
import { AdvancedMode } from "@/pages/AdvancedMode";
import { SlopeControls } from "@/components/height-map/SlopeControls";

export const PhotoshopLayout = ({
  mode,
  setMode,
  hasImage,
  imageMeta,
  handleFile,
  loadSample,
  resetAll,
  layers,
  updateLayer,
  deleteLayer,
  addLayer,
  reorderLayer,
  dragIndex,
  setDragIndex,
  allSolid,
  setAllSolidWithLayers,
  mapCoverage,
  layerCoverage,
  histogram,
  metric,
  setMetric,
  originalCanvasRef,
  colorCanvasRef,
  paintCanvasRef,
  baseImageData,
  exactColorImageData,
  paintOverlay,
  setPaintOverlay,
  paintSyncToken,
  activePaintLayerId,
  setActivePaintLayerId,
  imageVersion,
  showReference,
  setShowReference,
  referenceOpacity,
  setReferenceOpacity,
  sourceImageData,
  undo,
  canUndo,
  onPaintBeforeStroke,
  exportPalette,
  importPalette,
  importInputRef,
  open3D,
  setShowPresets,
  setShowSettings,
  setShowHelp,
  setShowColorMapImport,
  activePresetId,
  download,
  settings,
  showRobloxWarning,
  ROBLOX_PRESET_ID,
  customPatterns,
  addCombinedLayer,
  slopeAngles,
  slopeSettings,
  setSlopeSettings,
  ownerLayerIndices,
}) => {
  const replaceInputRef = useRef(null);
  const mainFileInputRef = useRef(null);
  const [advancedControls, setAdvancedControls] = useState(null);

  const handleFiles = (files) => {
    if (files && files[0]) {
      handleFile(files[0]);
    }
  };

  return (
    <div
      data-testid="photoshop-workspace"
      google-side-rail-overlap="false"
      className="h-screen w-screen overflow-hidden flex flex-col bg-[#08090c] text-zinc-100 select-none"
    >
      {/* TOP HEADER / TOOLBAR */}
      <header className="h-14 bg-[#101116] border-b border-zinc-800/80 px-4 flex items-center justify-between gap-3 z-40 shrink-0 shadow-lg shadow-black/20">
        {/* Left: Branding & Mode Tabs */}
        <div className="flex items-center gap-4">
          <button
            type="button"
            data-testid="logo-reset-btn"
            onClick={resetAll}
            title="Reset workspace"
            className="flex items-center gap-2.5 group text-left rounded-lg focus-visible:outline-none"
          >
            <div className="w-8 h-8 rounded-lg brand-gradient flex items-center justify-center shadow-md shadow-rose-500/20">
              <Layers className="w-4 h-4 text-white" strokeWidth={2.3} />
            </div>
            <div>
              <span className="text-sm font-bold tracking-tight text-zinc-100">
                Height Map <span className="text-rose-400">Studio</span>
              </span>
            </div>
          </button>

          {/* Mode Tabs on Top Toolbar */}
          <div className="flex items-center bg-[#0a0b0e] rounded-lg p-1 border border-zinc-800/80">
            <TopTabBtn
              testId="mode-gradient"
              active={mode === "gradient"}
              onClick={() => {
                playTabSound("gradient");
                setMode("gradient");
              }}
              icon={<Palette className="w-3.5 h-3.5" />}
              label="Elevation"
            />
            <TopTabBtn
              testId="mode-exact"
              active={mode === "exact"}
              onClick={() => {
                playTabSound("exact");
                setMode("exact");
              }}
              icon={<Sliders className="w-3.5 h-3.5" />}
              label="Manual Paint"
            />
            <TopTabBtn
              testId="mode-advanced"
              active={mode === "advanced"}
              onClick={() => {
                playTabSound("advanced");
                setMode("advanced");
              }}
              icon={<Sparkles className="w-3.5 h-3.5" />}
              label="Material Mix"
            />
            <TopTabBtn
              testId="mode-slope"
              active={mode === "slope"}
              onClick={() => {
                playTabSound("slope");
                setMode("slope");
              }}
              icon={<Mountain className="w-3.5 h-3.5" />}
              label="Steep Slopes"
            />
          </div>
        </div>

        {/* Header Right: Reference, 3D, Presets & Download on Very Top Right */}
        <div className="flex items-center gap-2">
          {/* Reference Overlay Button */}
          <div className="relative group">
            <button
              data-testid="reference-toggle"
              disabled={!hasImage}
              onClick={() => setShowReference(!showReference)}
              title={!hasImage ? "Upload a height map first" : "Overlay original height map"}
              className={`flex items-center gap-1.5 h-8 px-2.5 rounded-lg border text-xs font-medium transition ${
                !hasImage
                  ? "border-zinc-800/60 bg-zinc-900/40 text-zinc-600 cursor-not-allowed"
                  : showReference
                  ? "brand-gradient text-white border-transparent shadow-md shadow-rose-500/25"
                  : "border-zinc-800 bg-zinc-900/60 text-zinc-300 hover:text-white"
              }`}
            >
              <Eye className="w-3.5 h-3.5" />
              Reference
              <span className={`text-[9px] font-bold uppercase ${showReference ? "text-white" : "text-zinc-500"}`}>
                {showReference ? "On" : "Off"}
              </span>
            </button>
            {hasImage && (
              <div className="absolute right-0 top-full pt-2 z-[60] opacity-0 translate-y-1 pointer-events-none group-hover:opacity-100 group-hover:translate-y-0 group-hover:pointer-events-auto transition">
                <div className="w-52 rounded-xl border border-zinc-700 bg-[#141519] p-3 shadow-2xl">
                  <div className="flex items-center justify-between mb-2 text-xs">
                    <span className="text-[10px] uppercase text-zinc-500 font-semibold">Opacity</span>
                    <span className="mono text-zinc-300">{Math.round(referenceOpacity * 100)}%</span>
                  </div>
                  <input
                    data-testid="reference-opacity"
                    type="range"
                    min={0}
                    max={1}
                    step={0.05}
                    value={referenceOpacity}
                    onChange={(e) => setReferenceOpacity(Number(e.target.value))}
                    className="w-full accent-rose-500 cursor-pointer"
                  />
                </div>
              </div>
            )}
          </div>

          <button
            data-testid="3d-btn"
            onClick={open3D}
            title="3D Terrain View"
            className="h-8 px-2.5 rounded-lg bg-zinc-900/70 hover:bg-zinc-800 border border-zinc-800 flex items-center gap-1.5 text-xs font-medium text-zinc-200 transition"
          >
            <Box className="w-3.5 h-3.5 text-rose-400" />
            3D
          </button>

          <button
            data-testid="undo-btn"
            onClick={undo}
            disabled={!canUndo}
            title="Undo (Ctrl+Z)"
            className={`h-8 px-2.5 rounded-lg border flex items-center gap-1.5 text-xs font-medium transition ${
              canUndo
                ? "bg-zinc-900/70 hover:bg-zinc-800 border-zinc-800 text-zinc-200"
                : "bg-zinc-900/40 border-zinc-800/50 text-zinc-600 cursor-not-allowed"
            }`}
          >
            <Undo2 className="w-3.5 h-3.5" />
          </button>

          {/* Presets Button on Very Top Right */}
          <Button
            data-testid="presets-btn"
            variant="ghost"
            size="sm"
            onClick={() => setShowPresets(true)}
            className="h-8 accent-border-hover bg-zinc-900/80 hover:bg-zinc-800 border border-zinc-800 text-zinc-200 text-xs font-medium"
          >
            <Palette className="w-3.5 h-3.5 mr-1 text-rose-400" />
            Presets
            {activePresetId === ROBLOX_PRESET_ID && (
              <span data-testid="active-preset-status" className="ml-1 rounded-full border border-emerald-400/25 bg-emerald-500/10 px-1 py-0.2 text-[8px] font-semibold text-emerald-300">
                Roblox
              </span>
            )}
          </Button>

          {settings.showImportColorMap && (
            <button
              data-testid="import-color-map-btn"
              onClick={() => setShowColorMapImport(true)}
              title="Import an existing color map (Experimental)"
              className="relative flex h-8 items-center gap-1.5 rounded-lg border border-amber-400/25 bg-amber-500/5 px-2.5 text-xs font-medium text-amber-200 hover:border-amber-300/50 hover:bg-amber-500/10"
            >
              <Upload className="h-3.5 w-3.5" />
              Import
              <span className="h-1.5 w-1.5 rounded-full bg-amber-300 shadow-[0_0_8px_rgba(252,211,77,0.8)]" />
            </button>
          )}

          {/* Download Color Map Button on Very Top Right */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                data-testid="download-btn"
                size="sm"
                disabled={!hasImage}
                className="h-8 brand-gradient text-white text-xs font-semibold hover:opacity-95 disabled:opacity-40 disabled:cursor-not-allowed shadow-md shadow-rose-500/20"
              >
                <Download className="w-3.5 h-3.5 mr-1" />
                Download Color Map
                <ChevronDown className="w-3 h-3 ml-1 opacity-80" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              data-testid="download-format-menu"
              align="end"
              className="bg-[#141519] border-zinc-700 text-zinc-100"
            >
              <DropdownMenuLabel className="text-zinc-500 text-[10px] uppercase tracking-widest">
                Download Format
              </DropdownMenuLabel>
              <DropdownMenuItem
                data-testid="download-png"
                onClick={() => download("png")}
                className="group cursor-pointer focus:bg-rose-500/15 focus:text-white text-xs"
              >
                PNG <span className="ml-auto text-[10px] text-rose-300 font-semibold">Recommended</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="download-jpeg"
                onClick={() => download("jpeg")}
                className="cursor-pointer focus:bg-rose-500/15 focus:text-white text-xs"
              >
                JPG
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="download-webp"
                onClick={() => download("webp")}
                className="cursor-pointer focus:bg-rose-500/15 focus:text-white text-xs"
              >
                WEBP
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <button
            data-testid="settings-btn"
            onClick={() => setShowSettings(true)}
            className="w-8 h-8 rounded-lg bg-zinc-900/70 hover:bg-zinc-800 border border-zinc-800 flex items-center justify-center text-zinc-400 hover:text-zinc-100 transition"
            title="Settings"
          >
            <Settings className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => setShowHelp(true)}
            className="w-8 h-8 rounded-lg bg-zinc-900/70 hover:bg-zinc-800 border border-zinc-800 flex items-center justify-center text-zinc-400 hover:text-zinc-100 transition"
            title="Help"
          >
            <HelpCircle className="w-3.5 h-3.5" />
          </button>
        </div>
      </header>

      {/* MAIN CONTENT AREA */}
      <div className="flex-1 flex overflow-hidden relative">
        {/* LEFT SIDEBAR: Clean Height Layer Sliders & Controls */}
        <aside className="w-[360px] h-full bg-[#101116] border-r border-zinc-800/80 flex flex-col overflow-y-auto overflow-x-hidden p-4 space-y-4 scroll-thin shrink-0 z-20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Layers className="w-4 h-4 text-rose-400" />
              <span className="text-sm text-zinc-100 font-semibold">Height Layers</span>
              <span data-testid="layer-count" className="text-[11px] px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-300 mono">
                {layers.length}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button
                data-testid="import-palette-btn"
                title="Import palette JSON"
                onClick={() => importInputRef.current?.click()}
                className="w-7 h-7 rounded-lg text-zinc-400 hover:text-zinc-100 flex items-center justify-center hover:bg-zinc-800 transition"
              >
                <Upload className="w-3.5 h-3.5" />
              </button>
              <button
                data-testid="export-palette-btn"
                title="Export palette JSON"
                onClick={exportPalette}
                className="w-7 h-7 rounded-lg text-zinc-400 hover:text-zinc-100 flex items-center justify-center hover:bg-zinc-800 transition"
              >
                <Download className="w-3.5 h-3.5" />
              </button>
              <input
                ref={importInputRef}
                type="file"
                accept="application/json"
                hidden
                data-testid="import-palette-input"
                onChange={(e) => {
                  if (e.target.files?.[0]) importPalette(e.target.files[0]);
                  e.target.value = "";
                }}
              />
            </div>
          </div>

          <div className="flex items-center justify-between rounded-xl bg-[#0d0e12] border border-zinc-800/70 px-3.5 py-2">
            <span
              data-testid="all-solid-label"
              className={`text-xs font-medium ${allSolid ? "text-zinc-100" : "text-zinc-400"}`}
            >
              All Solid Colors
            </span>
            <Switch data-testid="all-solid-toggle" checked={allSolid} onCheckedChange={setAllSolidWithLayers} />
          </div>

          {settings.showStrip && (
            <PaletteStrip
              layers={layers}
              mapCoverage={mapCoverage}
              layerCoverage={layerCoverage}
              histogram={hasImage ? histogram : null}
              metric={metric}
              onMetricChange={setMetric}
            />
          )}

          <div className="flex-1 min-w-0 space-y-3 overflow-y-auto overflow-x-hidden scroll-thin pr-1 min-h-[240px]">
            {layers.map((layer, idx) => (
              <HeightLayerRow
                key={layer.id}
                layer={layer}
                layerPercentage={layerCoverage[idx] ?? 0}
                mapPercentage={mapCoverage[idx] ?? 0}
                hasImage={hasImage}
                disableSolidToggle={allSolid}
                showRobloxWaterWarning={showRobloxWarning && layer.name.toLowerCase() === "water"}
                onChange={(next) => updateLayer(idx, next)}
                onDelete={() => deleteLayer(idx)}
                isDragging={dragIndex === idx}
                onDragStart={(e) => {
                  setDragIndex(idx);
                  e.dataTransfer.effectAllowed = "move";
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  if (dragIndex !== null) reorderLayer(dragIndex, idx);
                  setDragIndex(null);
                }}
                onDragEnd={() => setDragIndex(null)}
              />
            ))}
          </div>

          <button
            data-testid="add-layer-btn"
            onClick={addLayer}
            className="accent-border-hover w-full flex items-center justify-center gap-2 py-2 rounded-xl border border-dashed border-zinc-700 text-zinc-400 hover:text-zinc-100 transition-all text-xs font-medium shrink-0"
          >
            <Plus className="w-4 h-4" />
            Add height layer
          </button>
        </aside>

        {/* CENTER VIEWPORT: HUGE GENERATED COLOR MAP FOCUS */}
        <main className="flex-1 h-full bg-[#07080a] relative flex flex-col items-center justify-center p-3 overflow-hidden">
          {!hasImage ? (
            /* Blank state dropzone centered */
            <div className="w-full max-w-xl p-8">
              <div
                data-testid="drop-zone"
                onClick={() => mainFileInputRef.current?.click()}
                className="border-2 border-dashed border-zinc-800 hover:border-zinc-700 bg-[#0d0e12]/80 rounded-2xl p-10 text-center cursor-pointer transition flex flex-col items-center justify-center gap-4"
              >
                <div className="w-16 h-16 rounded-2xl brand-gradient flex items-center justify-center shadow-xl shadow-rose-500/20">
                  <UploadCloud className="w-8 h-8 text-white" strokeWidth={2.2} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-zinc-100">Drop a Grayscale Height Map</h3>
                  <p className="text-xs text-zinc-500 mt-1">PNG / JPG / WEBP terrain map to generate full color map</p>
                </div>

                {loadSample && (
                  <div className="mt-3 flex flex-col items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    <span className="text-[10px] uppercase text-zinc-600 tracking-wider">Or load a sample terrain:</span>
                    <div className="flex gap-2">
                      {[
                        { id: "hills", label: "Rolling Hills" },
                        { id: "mountains", label: "Mountains" },
                        { id: "island", label: "Island" },
                      ].map((s) => (
                        <button
                          key={s.id}
                          data-testid={`sample-${s.id}`}
                          onClick={() => loadSample(s.id)}
                          className="px-3 py-1 rounded-full border border-zinc-700 bg-zinc-900 text-xs text-zinc-300 hover:text-white hover:border-rose-500 transition"
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <input
                  ref={mainFileInputRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => handleFiles(e.target.files)}
                />
              </div>
            </div>
          ) : (
            /* HUGE Main Viewport Canvas based on Active Mode */
            <div className="w-full h-full flex flex-col items-center justify-center relative overflow-hidden rounded-xl bg-[#090a0d] border border-zinc-800/80">
              {/* Floating Canvas Header Pill (Gradient and Advanced mode) */}
              {mode !== "exact" && (
                <div className="absolute top-3 left-1/2 -translate-x-1/2 z-30 flex items-center gap-3 bg-[#101116]/90 backdrop-blur-md px-4 py-1.5 rounded-xl border border-zinc-800/80 shadow-2xl text-xs">
                  <div className="flex items-center gap-2 text-zinc-400">
                    <span className="w-2.5 h-2.5 rounded-full brand-gradient" />
                    <span className="font-semibold text-zinc-200 uppercase tracking-wider text-[11px]">
                      {mode === "gradient"
                        ? "Elevation Color Map"
                        : mode === "slope"
                          ? "Steep Slope Material Map"
                          : "Material Mix View"}
                    </span>
                    {imageMeta && (
                      <span className="text-[10px] text-zinc-500 font-mono">
                        ({imageMeta.width} × {imageMeta.height} px)
                      </span>
                    )}
                  </div>
                  <div className="w-px h-3.5 bg-zinc-800" />
                  <button
                    data-testid="replace-image-btn"
                    onClick={() => replaceInputRef.current?.click()}
                    className="text-[11px] text-rose-400 hover:text-rose-300 font-medium transition"
                  >
                    Replace height map
                  </button>
                  <input
                    ref={replaceInputRef}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(e) => handleFiles(e.target.files)}
                  />
                </div>
              )}

              {/* Viewport Content — all modes fill 100% so canvases show behind the floating header pill */}
              {(mode === "gradient" || mode === "slope") && (
                <div className="absolute inset-0">
                  <ZoomCanvas testId="color-zoom" className="w-full h-full">
                    <canvas
                      data-testid="color-canvas"
                      ref={colorCanvasRef}
                      className={`max-w-full max-h-full object-contain ${settings.roundedMapPreview ? "rounded-xl" : ""}`}
                      style={{ imageRendering: "pixelated" }}
                    />
                  </ZoomCanvas>
                </div>
              )}

              {mode === "exact" && (
                <div className="absolute inset-0">
                    <PaintCanvas
                    baseImageData={exactColorImageData ?? baseImageData}
                    paintCanvasRef={paintCanvasRef}
                    layers={layers}
                    activeLayerId={activePaintLayerId || layers[0]?.id}
                    onSelectLayer={setActivePaintLayerId}
                    paintKey={imageVersion}
                    paintOverlay={paintOverlay}
                    paintSyncToken={paintSyncToken}
                    onOverlayChange={setPaintOverlay}
                    referenceImageData={sourceImageData}
                    showReference={showReference}
                    referenceOpacity={referenceOpacity}
                    onBeforeStroke={onPaintBeforeStroke}
                    onUndo={undo}
                      canUndo={canUndo}
                      floatingToolbar={true}
                      roundedMapPreview={settings.roundedMapPreview}
                  />
                </div>
              )}

              {mode === "advanced" && (
                <div className="absolute inset-0">
                  <AdvancedMode
                    sourceImageData={sourceImageData}
                    layers={layers}
                    mode="exact"
                    allSolid={allSolid}
                    bigViewports={true}
                    paintOverlay={paintOverlay}
                    showReference={showReference}
                    referenceOpacity={referenceOpacity}
                    customPatterns={customPatterns}
                    onSaveCombinedLayer={addCombinedLayer}
                    isImprovedUI={true}
                    onRenderControls={setAdvancedControls}
                    roundedMapPreview={settings.roundedMapPreview}
                  />
                </div>
              )}
            </div>
          )}
        </main>

        {/* RIGHT SIDEBAR: Height Map (Top Right) + How It Works / Advanced Combine Controls + Polaris Games Footer (Bottom Right) */}
        <aside className="w-[340px] h-full bg-[#101116] border-l border-zinc-800/80 p-3.5 flex flex-col justify-between overflow-y-auto scroll-thin shrink-0 z-20">
          <div className="space-y-4">
            {/* Top Right: Small Height Map Preview */}
            <div className="rounded-xl border border-zinc-800/80 bg-[#0d0e12] overflow-hidden p-2.5">
              <div className="flex items-center justify-between pb-2 border-b border-zinc-800/60 mb-2">
                <div className="flex items-center gap-1.5 text-xs text-zinc-300 font-medium">
                  <ImageIcon className="w-3.5 h-3.5 text-rose-400" />
                  <span>Height Map Preview</span>
                </div>
                {imageMeta && (
                  <span className="text-[10px] text-zinc-500 font-mono">
                    {imageMeta.width}×{imageMeta.height}
                  </span>
                )}
              </div>
              <div className="relative w-full h-32 rounded-lg bg-[#08090b] flex items-center justify-center overflow-hidden border border-zinc-800/60">
                {hasImage ? (
                  <canvas
                    data-testid="original-canvas"
                    ref={originalCanvasRef}
                    className={`max-w-full max-h-full object-contain ${settings.roundedMapPreview ? "rounded-xl" : ""}`}
                    style={{ imageRendering: "pixelated" }}
                  />
                ) : (
                  <div className="text-center p-3">
                    <p className="text-xs text-zinc-600">No height map loaded</p>
                  </div>
                )}
              </div>
            </div>

            {/* Middle Right: If Advanced Mode, render Combine Materials; otherwise render How It Works */}
            <div className="rounded-xl border border-zinc-800/80 bg-[#0d0e12] p-3">
              {mode === "advanced" ? (
                advancedControls ?? (
                  <div className="p-4 text-xs text-zinc-500 text-center">
                    Loading Material Mix controls...
                  </div>
                )
              ) : mode === "slope" ? (
                <SlopeControls
                  sourceImageData={sourceImageData}
                  slopeAngles={slopeAngles}
                  layers={layers}
                  slopeSettings={slopeSettings}
                  onChange={setSlopeSettings}
                  ownerLayerIndices={ownerLayerIndices}
                  compact={true}
                  roundedMapPreview={settings.roundedMapPreview}
                />
              ) : (
                <HowItWorks
                  onOpenHelp={() => setShowHelp(true)}
                  showRobloxWarning={showRobloxWarning}
                />
              )}
            </div>
          </div>

          {/* Bottom Right: A product of Polaris Games */}
          <div className="mt-4 pt-3 border-t border-zinc-800/80">
            <div
              data-testid="site-footer"
              className="rounded-xl border border-zinc-800/80 bg-[#0c0d11] p-3 flex flex-col gap-2.5"
            >
              <div className="flex items-center gap-2.5">
                <div className="w-7 h-7 rounded-lg brand-gradient flex items-center justify-center shadow-md shadow-rose-500/20 shrink-0">
                  <Layers className="w-3.5 h-3.5 text-white" strokeWidth={2.3} />
                </div>
                <div>
                  <div className="text-xs font-semibold text-zinc-100">
                    A product of Polaris Games
                  </div>
                  <div className="text-[10px] text-zinc-500">
                    Built for terrain artists
                  </div>
                </div>
              </div>
              <a
                data-testid="roblox-group-link"
                href="https://www.roblox.com/share/g/751583715"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full inline-flex items-center justify-center gap-1.5 rounded-lg brand-gradient py-1.5 text-xs font-semibold text-white shadow-sm shadow-rose-500/20 transition hover:opacity-95"
              >
                <Users className="w-3.5 h-3.5" />
                Join Roblox Group
              </a>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

const TopTabBtn = ({ active, onClick, icon, label, testId }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    className={`flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-semibold transition ${
      active
        ? "brand-gradient text-white shadow-sm shadow-rose-500/25"
        : "text-zinc-400 hover:bg-zinc-800/60 hover:text-zinc-200"
    }`}
  >
    {icon}
    {label}
  </button>
);

let tabAudioContext;

const playTabSound = (tab) => {
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;
    tabAudioContext ??= new AudioContext();
    if (tabAudioContext.state === "suspended") tabAudioContext.resume();
    const tones = {
      gradient: [261.63, 392],
      exact: [329.63, 493.88],
      advanced: [523.25, 659.25, 783.99],
      slope: [220, 293.66, 440],
    }[tab] ?? [392];
    const start = tabAudioContext.currentTime;
    tones.forEach((frequency, index) => {
      const oscillator = tabAudioContext.createOscillator();
      const gain = tabAudioContext.createGain();
      const noteStart = start + index * 0.045;
      oscillator.type = tab === "advanced" ? "triangle" : "sine";
      oscillator.frequency.setValueAtTime(frequency, noteStart);
      gain.gain.setValueAtTime(0.0001, noteStart);
      gain.gain.exponentialRampToValueAtTime(0.035, noteStart + 0.012);
      gain.gain.exponentialRampToValueAtTime(0.0001, noteStart + 0.16);
      oscillator.connect(gain);
      gain.connect(tabAudioContext.destination);
      oscillator.start(noteStart);
      oscillator.stop(noteStart + 0.17);
    });
  } catch {
    // Audio feedback is an enhancement; mode switching must always keep working.
  }
};
